/**
 * Offline rendering to WAV.
 *
 * Uses the same RenderEngine as live playback, so an export is a bit-exact
 * capture of what the speakers were doing — no separate "export path" that can
 * drift away from the player.
 */
import { RenderEngine } from '../engine';
import type { MixerSettings } from '../dsp/mixer';
import type { Song } from '../types';

export interface RenderOptions {
  sampleRate: number;
  /** Hard ceiling in seconds, so a module that never ends still terminates. */
  maxSeconds: number;
  /** Stop as soon as the song wraps rather than at maxSeconds. */
  stopAtSongEnd: boolean;
  /** Extra seconds rendered after the end so reverb tails and loops ring out. */
  tailSeconds: number;
  mixer?: Partial<MixerSettings>;
  onProgress?: (fraction: number) => void;
}

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  sampleRate: 44100,
  maxSeconds: 600,
  stopAtSongEnd: true,
  tailSeconds: 2,
};

export interface RenderedAudio {
  left: Float32Array;
  right: Float32Array;
  sampleRate: number;
  seconds: number;
}

/** Render a song to raw stereo float buffers. */
export function renderSong(song: Song, options: Partial<RenderOptions> = {}): RenderedAudio {
  const opts = { ...DEFAULT_RENDER_OPTIONS, ...options };
  const engine = new RenderEngine(song, opts.sampleRate, {
    mixer: opts.mixer,
    player: { loopSong: false },
  });
  engine.play();

  const block = 4096;
  const maxFrames = Math.ceil(opts.maxSeconds * opts.sampleRate);
  const tailFrames = Math.ceil(opts.tailSeconds * opts.sampleRate);

  const chunks: { l: Float32Array; r: Float32Array }[] = [];
  const bufL = new Float32Array(block);
  const bufR = new Float32Array(block);

  let total = 0;
  let tailRemaining = -1;

  while (total < maxFrames) {
    engine.process(bufL, bufR, block);
    chunks.push({ l: bufL.slice(0), r: bufR.slice(0) });
    total += block;

    if (tailRemaining >= 0) {
      tailRemaining -= block;
      if (tailRemaining <= 0) break;
    } else if (opts.stopAtSongEnd && engine.player.songEnded) {
      tailRemaining = tailFrames;
    }

    opts.onProgress?.(Math.min(1, total / maxFrames));
  }

  const left = new Float32Array(total);
  const right = new Float32Array(total);
  let pos = 0;
  for (const c of chunks) {
    left.set(c.l, pos);
    right.set(c.r, pos);
    pos += c.l.length;
  }

  opts.onProgress?.(1);
  return { left, right, sampleRate: opts.sampleRate, seconds: total / opts.sampleRate };
}

/** Wrap stereo float buffers as a 16-bit PCM WAV file. */
export function encodeWav(audio: RenderedAudio, bitDepth: 16 | 24 | 32 = 16): Uint8Array {
  const { left, right, sampleRate } = audio;
  const frames = left.length;
  const channels = 2;
  const bytesPerSample = bitDepth / 8;
  const dataSize = frames * channels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  // Format 3 is IEEE float; anything else here is integer PCM.
  view.setUint16(20, bitDepth === 32 ? 3 : 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * bytesPerSample, true);
  view.setUint16(32, channels * bytesPerSample, true);
  view.setUint16(34, bitDepth, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < frames; i++) {
    for (const channel of [left, right]) {
      const v = Math.max(-1, Math.min(1, channel[i]));
      if (bitDepth === 16) {
        view.setInt16(offset, Math.round(v * 32767), true);
        offset += 2;
      } else if (bitDepth === 24) {
        const q = Math.round(v * 8388607);
        view.setUint8(offset, q & 0xff);
        view.setUint8(offset + 1, (q >> 8) & 0xff);
        view.setUint8(offset + 2, (q >> 16) & 0xff);
        offset += 3;
      } else {
        view.setFloat32(offset, v, true);
        offset += 4;
      }
    }
  }

  return new Uint8Array(buffer);
}
